import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Divider,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  alpha,
  Fade,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Other as OtherIcon,
  CheckCircle as CheckCircleIcon,
  Navigation as NavigationIcon,
  Store as StoreIcon,
  LocalShipping as DeliveryIcon,
} from "@mui/icons-material";
import { FetchAddress, CreateAddress, EditAddress, DeleteAddress, DeliveryForZipcode ,DefaultAddress} from "../../services/api/cartService";

const AddressDrawer = ({ open, onClose, onAddressSelect, selectedAddressId, userId, zipcode }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(selectedAddressId);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deliveryStatus, setDeliveryStatus] = useState({});
  const [isCheckingDelivery, setIsCheckingDelivery] = useState(false);
  
  // Refs to prevent duplicate API calls
  const hasFetchedAddresses = useRef(false);
  const deliveryCheckCache = useRef(new Map());
  const pendingDeliveryChecks = useRef(new Map());
  
  // Form state
  const [formData, setFormData] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "IN",
    addressLabel: "Address",
    isDefault: false,
    deliveryInstructions: "",
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  // Fetch addresses when drawer opens - only once
  useEffect(() => {
    if (open && userId && !hasFetchedAddresses.current) {
      hasFetchedAddresses.current = true;
      fetchAddresses();
    }
    
    // Reset the flag when drawer closes
    if (!open) {
      hasFetchedAddresses.current = false;
    }
  }, [open, userId]);

  // Reset delivery status when addresses change
  useEffect(() => {
    if (addresses.length > 0) {
      checkDeliveryForAllAddresses();
    }
  }, [addresses]);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await FetchAddress(userId);
      console.log("FetchAddress Response:", response);
      
      if (response.data?.success && response.data?.data) {
        setAddresses(response.data.data);
        
        // If no address selected but there's a default, select it
        if (!selectedId && response.data.data.length > 0) {
          const defaultAddr = response.data.data.find(addr => addr.isDefault);
          if (defaultAddr) {
            setSelectedId(defaultAddr.id);
            onAddressSelect?.(defaultAddr.id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      showSnackbar("Failed to load addresses", "error");
    } finally {
      setLoading(false);
    }
  };

  // Check delivery for all addresses with debouncing and caching
  const checkDeliveryForAllAddresses = useCallback(async () => {
    if (addresses.length === 0) return;
    
    setIsCheckingDelivery(true);
    
    // Create an array of unique postal codes to check
    const uniquePostalCodes = [...new Set(addresses.map(addr => addr.postalCode))];
    
    // Check delivery for each unique postal code
    const checkPromises = uniquePostalCodes.map(async (postalCode) => {
      // Check cache first
      if (deliveryCheckCache.current.has(postalCode)) {
        const cachedResult = deliveryCheckCache.current.get(postalCode);
        // Update status for all addresses with this postal code
        addresses.forEach(address => {
          if (address.postalCode === postalCode) {
            setDeliveryStatus(prev => ({
              ...prev,
              [address.id]: cachedResult
            }));
          }
        });
        return;
      }
      
      // Check if there's already a pending request for this postal code
      if (pendingDeliveryChecks.current.has(postalCode)) {
        const pendingPromise = pendingDeliveryChecks.current.get(postalCode);
        const result = await pendingPromise;
        addresses.forEach(address => {
          if (address.postalCode === postalCode) {
            setDeliveryStatus(prev => ({
              ...prev,
              [address.id]: result
            }));
          }
        });
        return;
      }
      
      // Create new request
      const requestPromise = checkSingleDelivery(postalCode);
      pendingDeliveryChecks.current.set(postalCode, requestPromise);
      
      try {
        const isAvailable = await requestPromise;
        // Cache the result
        deliveryCheckCache.current.set(postalCode, isAvailable);
        
        // Update status for all addresses with this postal code
        addresses.forEach(address => {
          if (address.postalCode === postalCode) {
            setDeliveryStatus(prev => ({
              ...prev,
              [address.id]: isAvailable
            }));
          }
        });
      } catch (error) {
        console.error(`Error checking delivery for ${postalCode}:`, error);
        addresses.forEach(address => {
          if (address.postalCode === postalCode) {
            setDeliveryStatus(prev => ({
              ...prev,
              [address.id]: false
            }));
          }
        });
      } finally {
        pendingDeliveryChecks.current.delete(postalCode);
      }
    });
    
    await Promise.all(checkPromises);
    setIsCheckingDelivery(false);
  }, [addresses]);

  const checkSingleDelivery = async (postalCode) => {
    try {
      const response = await DeliveryForZipcode(postalCode);
      console.log(`Delivery check for ${postalCode}:`, response);
      
      if (response.data?.success) {
        return response.data.data.isAvailable;
      }
      return false;
    } catch (error) {
      console.error("Error checking delivery:", error);
      return false;
    }
  };

  // Check delivery for a single new address (when adding/updating)
  const checkDeliveryForNewAddress = async (postalCode, addressId) => {
    // Check cache first
    if (deliveryCheckCache.current.has(postalCode)) {
      const isAvailable = deliveryCheckCache.current.get(postalCode);
      setDeliveryStatus(prev => ({
        ...prev,
        [addressId]: isAvailable
      }));
      return isAvailable;
    }
    
    // Check for pending request
    if (pendingDeliveryChecks.current.has(postalCode)) {
      const isAvailable = await pendingDeliveryChecks.current.get(postalCode);
      setDeliveryStatus(prev => ({
        ...prev,
        [addressId]: isAvailable
      }));
      return isAvailable;
    }
    
    // Make new request
    const requestPromise = checkSingleDelivery(postalCode);
    pendingDeliveryChecks.current.set(postalCode, requestPromise);
    
    try {
      const isAvailable = await requestPromise;
      deliveryCheckCache.current.set(postalCode, isAvailable);
      setDeliveryStatus(prev => ({
        ...prev,
        [addressId]: isAvailable
      }));
      return isAvailable;
    } finally {
      pendingDeliveryChecks.current.delete(postalCode);
    }
  };

  const handleSelectAddress = (addressId) => {
    console.log("Selecting address with ID:", addressId); 
    const address = addresses.find(addr => addr.id === addressId);
    console.log("Selected address details:", address);
    if (address && deliveryStatus[addressId]) {
      DefaultAddress(address);
      setSelectedId(addressId);
      onAddressSelect?.(addressId);
      showSnackbar("Address selected successfully", "success");
      onClose();
    } else if (address && !deliveryStatus[addressId]) {
      showSnackbar("Delivery not available for this pincode", "error");
    }
  };

  const handleAddAddress = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        entityId: userId,
        entityType: "consumer",
        latitude: "0",
        longitude: "0",
      };
      
      const response = await CreateAddress(payload);
      console.log("CreateAddress Response:", response);
      
      if (response.data?.success) {
        showSnackbar("Address added successfully", "success");
        await fetchAddresses();
        resetForm();
        setAddDialogOpen(false);
      } else {
        showSnackbar(response.data?.message || "Failed to add address", "error");
      }
    } catch (error) {
      console.error("Error adding address:", error);
      showSnackbar("Failed to add address", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAddress = async () => {
    if (!validateForm() || !editingAddress) return;
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        id: editingAddress.id,
        entityId: userId,
        entityType: "consumer",
      };
      
      const response = await EditAddress(editingAddress.id, payload);
      console.log("EditAddress Response:", response);
      
      if (response.data?.success) {
        showSnackbar("Address updated successfully", "success");
        await fetchAddresses();
        resetForm();
        setEditDialogOpen(false);
        setEditingAddress(null);
      } else {
        showSnackbar(response.data?.message || "Failed to update address", "error");
      }
    } catch (error) {
      console.error("Error updating address:", error);
      showSnackbar("Failed to update address", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!addressToDelete) return;
    
    setSubmitting(true);
    try {
      const response = await DeleteAddress(addressToDelete.id);
      console.log("DeleteAddress Response:", response);
      
      if (response.data?.success) {
        showSnackbar("Address deleted successfully", "success");
        
        // Clear cache for this postal code if no other address uses it
        const otherAddressWithSamePostalCode = addresses.some(
          addr => addr.id !== addressToDelete.id && addr.postalCode === addressToDelete.postalCode
        );
        
        if (!otherAddressWithSamePostalCode) {
          deliveryCheckCache.current.delete(addressToDelete.postalCode);
        }
        
        await fetchAddresses();
        
        if (selectedId === addressToDelete.id) {
          const newDefault = addresses.find(addr => addr.id !== addressToDelete.id && addr.isDefault);
          if (newDefault) {
            setSelectedId(newDefault.id);
            onAddressSelect?.(newDefault.id);
          } else if (addresses.length > 1) {
            const firstAddress = addresses.find(addr => addr.id !== addressToDelete.id);
            setSelectedId(firstAddress.id);
            onAddressSelect?.(firstAddress.id);
          }
        }
        
        setDeleteDialogOpen(false);
        setAddressToDelete(null);
      } else {
        showSnackbar(response.data?.message || "Failed to delete address", "error");
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      showSnackbar("Failed to delete address", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.addressLine1.trim()) errors.addressLine1 = "Address line 1 is required";
    if (!formData.city.trim()) errors.city = "City is required";
    if (!formData.state.trim()) errors.state = "State is required";
    if (!formData.postalCode.trim()) errors.postalCode = "Postal code is required";
    if (formData.postalCode.trim() && !/^\d{6}$/.test(formData.postalCode)) errors.postalCode = "Invalid postal code";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "IN",
      addressLabel: "Address",
      isDefault: false,
      deliveryInstructions: "",
    });
    setFormErrors({});
    setEditingAddress(null);
  };

  const openEditDialog = (address) => {
    setEditingAddress(address);
    setFormData({
      addressLine1: address.addressLine1 || "",
      addressLine2: address.addressLine2 || "",
      city: address.city || "",
      state: address.state || "",
      postalCode: address.postalCode || "",
      country: address.country || "IN",
      addressLabel: address.addressLabel || "Address",
      isDefault: address.isDefault || false,
      deliveryInstructions: address.deliveryInstructions || "",
    });
    setEditDialogOpen(true);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar(prev => ({ ...prev, open: false })), 3000);
  };

  const getAddressIcon = (label) => {
    switch (label?.toLowerCase()) {
      case "home":
      case "primary":
        return <HomeIcon fontSize="small" />;
      case "work":
      case "office":
        return <WorkIcon fontSize="small" />;
      default:
        return <LocationIcon fontSize="small" />;
    }
  };

  const getAddressLabelColor = (label) => {
    switch (label?.toLowerCase()) {
      case "home":
      case "primary":
        return "#4caf50";
      case "work":
      case "office":
        return "#2196f3";
      default:
        return "#ff9800";
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 550 },
            maxWidth: "100%",
            borderRadius: { xs: 0, sm: "24px 0 0 24px" },
            overflow: "hidden",
            bgcolor: "#fafafa",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "linear-gradient(135deg, #f6cc4f 0%, #ffe8a2 50%, #f6cc4f 100%)",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <LocationIcon sx={{ fontSize: 28, color: "#3a2e00" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: 20,
                color: "#3a2e00",
              }}
            >
              Select Delivery Address
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: "rgba(0,0,0,0.08)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.15)" },
            }}
          >
            <CloseIcon sx={{ color: "#3a2e00" }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
          {loading || isCheckingDelivery ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Add New Address Button */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  borderColor: "#f6cc4f",
                  color: "#3a2e00",
                  py: 1.5,
                  "&:hover": {
                    borderColor: "#f6cc4f",
                    bgcolor: alpha("#f6cc4f", 0.05),
                  },
                }}
              >
                Add New Address
              </Button>

              {/* Address List */}
              <Stack spacing={2}>
                {addresses.map((address) => {
                  const isDeliverable = deliveryStatus[address.id];
                  
                  return (
                    <Fade in key={address.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: selectedId === address.id 
                            ? "2px solid #f6cc4f" 
                            : "1px solid #e0e0e0",
                          bgcolor: selectedId === address.id 
                            ? alpha("#f6cc4f", 0.05) 
                            : "#fff",
                          transition: "all 0.2s ease",
                          cursor: isDeliverable ? "pointer" : "default",
                          opacity: isDeliverable ? 1 : 0.7,
                          "&:hover": isDeliverable && {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            transform: "translateY(-2px)",
                          },
                        }}
                        onClick={() => isDeliverable && handleSelectAddress(address.id)}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                bgcolor: alpha(getAddressLabelColor(address.addressLabel), 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: getAddressLabelColor(address.addressLabel),
                              }}
                            >
                              {getAddressIcon(address.addressLabel)}
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                {address.addressLabel.charAt(0).toUpperCase() + address.addressLabel.slice(1)}
                              </Typography>
                              {address.isDefault && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    bgcolor: "#f6cc4f",
                                    color: "#3a2e00",
                                    fontWeight: 600,
                                    mt: 0.5,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditDialog(address);
                              }}
                              sx={{ color: "#666" }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddressToDelete(address);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{ color: "#d32f2f" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Typography sx={{ fontSize: 13, color: "#555", mb: 0.5 }}>
                          {address.addressLine1}
                        </Typography>
                        {address.addressLine2 && (
                          <Typography sx={{ fontSize: 13, color: "#555", mb: 0.5 }}>
                            {address.addressLine2}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 13, color: "#555", mb: 0.5 }}>
                          {address.city}, {address.state} - {address.postalCode}
                        </Typography>
                        
                        {/* Delivery Status Indicator */}
                        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                          {isDeliverable !== undefined ? (
                            isDeliverable ? (
                              <>
                                <DeliveryIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                                <Typography sx={{ fontSize: 12, color: "#4caf50", fontWeight: 600 }}>
                                  Delivery Available
                                </Typography>
                                {selectedId === address.id && (
                                  <CheckCircleIcon sx={{ fontSize: 16, color: "#f6cc4f", ml: "auto" }} />
                                )}
                              </>
                            ) : (
                              <>
                                <StoreIcon sx={{ fontSize: 16, color: "#f44336" }} />
                                <Typography sx={{ fontSize: 12, color: "#f44336", fontWeight: 600 }}>
                                  Delivery Not Available
                                </Typography>
                              </>
                            )
                          ) : (
                            <CircularProgress size={16} sx={{ color: "#f6cc4f" }} />
                          )}
                        </Box>
                      </Paper>
                    </Fade>
                  );
                })}
              </Stack>

              {addresses.length === 0 && (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <LocationIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                  <Typography sx={{ color: "#666" }}>No addresses found</Typography>
                  <Typography sx={{ color: "#999", fontSize: 13 }}>
                    Add a new address to get started
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* Add/Edit Address Dialog */}
      <Dialog
        open={addDialogOpen || editDialogOpen}
        onClose={() => {
          resetForm();
          setAddDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editDialogOpen ? "Edit Address" : "Add New Address"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl component="fieldset">
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Address Label</Typography>
              <RadioGroup
                row
                value={formData.addressLabel}
                onChange={(e) => setFormData({ ...formData, addressLabel: e.target.value })}
              >
                <FormControlLabel value="Home" control={<Radio />} label="Home" />
                <FormControlLabel value="Work" control={<Radio />} label="Work" />
                <FormControlLabel value="Address" control={<Radio />} label="Other" />
              </RadioGroup>
            </FormControl>

            <TextField
              label="Address Line 1 *"
              fullWidth
              size="small"
              value={formData.addressLine1}
              onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
              error={!!formErrors.addressLine1}
              helperText={formErrors.addressLine1}
            />

            <TextField
              label="Address Line 2"
              fullWidth
              size="small"
              value={formData.addressLine2}
              onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
            />

            <TextField
              label="City *"
              fullWidth
              size="small"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              error={!!formErrors.city}
              helperText={formErrors.city}
            />

            <TextField
              label="State *"
              fullWidth
              size="small"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              error={!!formErrors.state}
              helperText={formErrors.state}
            />

            <TextField
              label="Postal Code *"
              fullWidth
              size="small"
              value={formData.postalCode}
              onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
              error={!!formErrors.postalCode}
              helperText={formErrors.postalCode}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <CheckCircleIcon 
                  sx={{ 
                    color: formData.isDefault ? "#f6cc4f" : "#ccc",
                    cursor: "pointer",
                  }}
                  onClick={() => setFormData({ ...formData, isDefault: !formData.isDefault })}
                />
              }
              label="Set as default address"
              sx={{ "& .MuiTypography-root": { fontSize: 13 } }}
            />

            <TextField
              label="Delivery Instructions (Optional)"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={formData.deliveryInstructions}
              onChange={(e) => setFormData({ ...formData, deliveryInstructions: e.target.value })}
              placeholder="E.g., Gate code, landmark, etc."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              resetForm();
              setAddDialogOpen(false);
              setEditDialogOpen(false);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={editDialogOpen ? handleUpdateAddress : handleAddAddress}
            disabled={submitting}
            variant="contained"
            sx={{
              bgcolor: "#f6cc4f",
              color: "#3a2e00",
              "&:hover": { bgcolor: "#ffe8a2" },
            }}
          >
            {submitting ? <CircularProgress size={24} /> : (editDialogOpen ? "Update" : "Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Address</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this address? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAddress}
            disabled={submitting}
            variant="contained"
            sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#c62828" } }}
          >
            {submitting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddressDrawer;